import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { TableEditColumn, TableFilterRow } from '@devexpress/dx-react-grid-material-ui';
import { useMutation, useQuery } from '@apollo/client';
import { ChangeSet, Sorting } from '@devexpress/dx-react-grid';
import saveAs from 'file-saver';
import { GridExporter } from '@devexpress/dx-react-grid-export';

import { MenuItem, Select, TableCell } from '@mui/material';
import { Receipt as ReceiptType } from 'global-intern-types';
import { MONTHS } from '../../../../services/config/CONST';
// Local imports
import Table from '../../../../components/Table/Table';
import { columns } from './columnDefinition';
import DataContext from '../../../../context/DataProvider/DataContext';
import columnsWidths from './columnsWidths';
import getReceiptsPaginated from '../../../../services/requestToServerSchemas/querySchemas/getReceiptsPaginated';
import deleteReceiptQuery from '../../../../Apollo/mutations/deleteReceipt';
import AlertMessage from '../../../../components/AlertMessage/AlertMessage';

// @ts-expect-error
const onSaveXlsx = (workbook) => {
  // @ts-expect-error
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
  });
};

// @ts-expect-error
const onSaveFilterXlsx = (workbook) => {
  // @ts-expect-error
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'FilterDataGrid.xlsx');
  });
};

interface Row extends TableEditColumn.CellProps {
  row: ReceiptType;
}

// const defaultFilter = [{ columnName: 'exported_at', value: defaultFilterAndFormateValue }];

function EditCellForCommand(props: Row) {
  const { row } = props;
  if (row.exported_at) {
    // eslint-disable-next-line react/jsx-props-no-spreading
    return <TableEditColumn.Cell {...props}>{null}</TableEditColumn.Cell>;
  }
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TableEditColumn.Cell {...props} />;
}

function CustomFilterCell({ filter, onFilter, column, ...props }: TableFilterRow.CellProps) {
  if (column.name === 'month') {
    return (
      <TableCell>
        <Select
          value={filter ? filter.value : ''}
          onChange={(e) => onFilter(e.target.value ? { value: e.target.value, columnName: column.name } : null)}
          displayEmpty
          variant="standard"
          fullWidth
          style={{ width: '100%' }}
        >
          <MenuItem value="">Alle</MenuItem>
          {MONTHS.map((month) => (
            <MenuItem key={month.id} value={month.title}>
              {month.title}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
    );
  }
  if (column.name === 'type') {
    return (
      <TableCell>
        <Select
          value={filter ? filter.value : ''}
          onChange={(e) => onFilter(e.target.value ? { value: e.target.value, columnName: column.name } : null)}
          displayEmpty
          variant="standard"
          fullWidth
          style={{ width: '100%' }}
        >
          <MenuItem value="">Alle</MenuItem>
          <MenuItem value="IncomeReceipt">Eingehend</MenuItem>
          <MenuItem value="ExpenseReceipt">Ausgehend</MenuItem>
        </Select>
      </TableCell>
    );
  }
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <TableFilterRow.Cell filter={filter} onFilter={onFilter} column={column} {...props} />;
}

type Props = {
  agentId?: string; // Agent DatabaseID
};

function AllReceiptsTable({ agentId }: Props) {
  const [deleteReceipt, { error, data }] = useMutation(deleteReceiptQuery);

  const { loaderFun, user, bookingAccount, tax } = useContext(DataContext);

  // Server-side state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  const [sorting, setSorting] = useState<Sorting[]>([{ columnName: 'id', direction: 'desc' }]);
  const [search, setSearch] = useState('');
  // Filters mapped to backend
  // Default filter: exported_at = 'neu' (null)
  const [filters, setFilters] = useState<any[]>([{ columnName: 'exported_at', value: 'neu' }]);

  // Construct filters object for backend
  const backendFilters = useMemo(() => {
    const f: any = {};
    if (agentId) {
      f.agent_id = agentId;
    }
    filters.forEach((filter) => {
      if (filter.value !== undefined && filter.value !== null && filter.value !== '') {
        f[filter.columnName] = filter.value;
      }
    });
    return f;
  }, [agentId, filters]);

  const { data: queryData, refetch } = useQuery(getReceiptsPaginated, {
    variables: {
      page: currentPage,
      pageSize,
      sortField: sorting[0]?.columnName,
      sortOrder: sorting[0]?.direction?.toUpperCase(),
      filters: backendFilters,
      search,
    },
    fetchPolicy: 'network-only', // Ensure fresh data
  });

  const rows = queryData?.getReceiptsPaginated?.rows || [];
  const totalCount = queryData?.getReceiptsPaginated?.totalCount || 0;

  // Handlers
  const handlePageChange = (page: number) => setCurrentPage(page);
  const handleSortingChange = (newSorting: any[]) => setSorting(newSorting);
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(0);
  };
  const handleFiltersChange = (newFilters: any[]) => {
    setFilters(newFilters);
    setCurrentPage(0); // Reset to first page on filter change
  };

  /*
  useEffect(() => {
    setRow(agentId ? receipt.filter((item) => item.agent_id === agentId) : receipt);
  }, [agentId, receipt]);
  */

  useEffect(() => {
    if (tax.length === 0) {
      loaderFun.loadTax();
    }
    // if (receipt.length === 0) {
    //   loaderFun.loadReceipt();
    // }
    if (user.length === 0) {
      loaderFun.loadUser();
    }
  }, []);

  const refExportApp = useRef<typeof GridExporter>(null);
  const startExport = useCallback(() => {
    refExportApp.current?.exportGrid();
  }, [refExportApp]);

  const refExportFilter = useRef<typeof GridExporter>(null);
  const startFilterExport = useCallback(() => {
    refExportFilter.current?.exportGrid({ selectedOnly: true });
  }, [refExportFilter]);

  const handleCommitChanges = async ({ deleted }: ChangeSet) => {
    if (deleted) {
      const deletedSet = new Set(deleted);
      const setIter = deletedSet.values();
      const val = setIter.next().value;
      if (val !== undefined) {
        const idDelete = parseInt(String(val), 10);
        try {
          await deleteReceipt({ variables: { id: idDelete } });
          // Refresh table from server
          refetch();
        } catch (e) {
          console.error('Error deleting receipt:', e);
        }
      }
    }
  };

  return (
    <>
      <Table
        defaultColumnWidths={columnsWidths}
        FilterRow
        row={rows}
        columns={columns(user, bookingAccount, tax)}
        onCommitChanges={handleCommitChanges}
        EditCellForCommand={EditCellForCommand}
        editable
        showDeleteCommand
        pageSizes={[10, 20, 50, 200]}
        // Server-side props
        totalCount={totalCount}
        currentPage={currentPage}
        onCurrentPageChange={handlePageChange}
        externalSorting={sorting}
        onSortingChange={handleSortingChange}
        externalFiltering={filters}
        onFiltersChange={handleFiltersChange}
        searchValue={search}
        onSearchChange={handleSearchChange}
        FilterCell={CustomFilterCell}
        // filteringState={defaultFilter} // Replaced by managed state

        exporting={{
          all: {
            export: true,
            refConst: refExportApp,
            rows,
            columns: columns(user, bookingAccount, tax),
            onSave: onSaveXlsx,
            exportFunk: startExport,
          },
          filter: {
            export: true,
            refConst: refExportFilter,
            columns: columns(user, bookingAccount, tax),
            onSave: onSaveFilterXlsx,
            exportFunk: startFilterExport,
          },
        }}
      />
      <AlertMessage
        typeMessage={error ? 'error' : undefined}
        alertMessage={error ? 'Es ist ein Fehler aufgetreten' : undefined}
        isAlertMessage={!!error}
      />
      <AlertMessage
        typeMessage={data && !error ? 'success' : undefined}
        alertMessage={data && !error ? 'Beleg wurde erfolgreich gelöscht' : undefined}
        isAlertMessage={data && !error}
      />
    </>
  );
}

export default AllReceiptsTable;
